import { pushToDataLayer } from "./initTagManager";
import { ga4SendEcommerceEvent } from "./ga4Gtag";
import { hasAnalyticsConsent } from "./cookieConsent";
import {
  trackMetaAddToCart,
  trackMetaPurchase,
  trackMetaViewContent,
} from "./metaPixel";
import type { CartItem } from "../context/CartContext";

const API_BASE = process.env.REACT_APP_API_URL || "";

/** GA4 / GTM: samma valuta som WooCommerce */
const DEFAULT_CURRENCY = "SEK";

const PURCHASE_SESSION_PREFIX = "trir_ga_purchase_";

export type Ga4Item = {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_variant?: string;
};

function clearEcommerceObject(): void {
  pushToDataLayer({ ecommerce: null });
}

function pushEcommerceEvent(
  event: string,
  ecommerce: Record<string, unknown>
): void {
  clearEcommerceObject();
  pushToDataLayer({ event, ecommerce });
  if (hasAnalyticsConsent()) {
    ga4SendEcommerceEvent(event, { ...ecommerce });
  }
}

function cartLineToGa4Item(item: CartItem): Ga4Item {
  return {
    item_id: String(item.id),
    item_name: item.name,
    price: item.price,
    quantity: item.quantity,
  };
}

/** GA4: add_to_cart (anropas från CartContext vid varje tillägg) */
export function trackAddToCart(item: CartItem): void {
  const line = cartLineToGa4Item(item);
  const value = line.price * line.quantity;
  pushEcommerceEvent("add_to_cart", {
    currency: DEFAULT_CURRENCY,
    value,
    items: [line],
  });
  trackMetaAddToCart({
    content_ids: [line.item_id],
    content_name: line.item_name,
    content_type: "product",
    value,
    currency: DEFAULT_CURRENCY,
  });
}

/** GA4: view_item (produktsida) */
export function trackViewItem(items: Ga4Item[], value: number): void {
  pushEcommerceEvent("view_item", {
    currency: DEFAULT_CURRENCY,
    value,
    items,
  });
  if (items.length > 0) {
    trackMetaViewContent({
      content_ids: items.map((item) => item.item_id),
      content_name: items[0].item_name,
      content_type: "product",
      value,
      currency: DEFAULT_CURRENCY,
    });
  }
}

type OrderDetailsLineApi = {
  name: string;
  productId: number;
  variationId: number;
  quantity: number;
  price: string;
};

type OrderDetailsResponse = {
  id: number;
  currency?: string;
  total: string;
  shippingTotal?: string;
  totalTax?: string;
  lineItems: OrderDetailsLineApi[];
  couponLines?: { code: string }[];
};

function lineItemToGa4Item(row: OrderDetailsLineApi): Ga4Item {
  const id = row.variationId > 0 ? row.variationId : row.productId;
  const unit = parseFloat(String(row.price)) || 0;
  return {
    item_id: String(id),
    item_name: row.name,
    price: unit,
    quantity: Number(row.quantity) || 1,
  };
}

/**
 * GA4: purchase — radartiklar från Woo (samma källa som kvittot).
 * Idempotent per order (sessionStorage) så Stripe-retur / reload inte dubblerar.
 */
export async function trackPurchaseFromOrder(
  orderId: number | string
): Promise<void> {
  if (typeof window === "undefined") return;
  const key = `${PURCHASE_SESSION_PREFIX}${orderId}`;
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, "1");

  let data: OrderDetailsResponse;
  try {
    const res = await fetch(`${API_BASE}/api/orders/${orderId}/details`);
    if (!res.ok) {
      sessionStorage.removeItem(key);
      return;
    }
    data = (await res.json()) as OrderDetailsResponse;
  } catch {
    sessionStorage.removeItem(key);
    return;
  }

  if (!data.lineItems?.length) {
    sessionStorage.removeItem(key);
    return;
  }

  const items = data.lineItems.map((row) => lineItemToGa4Item(row));
  const value = parseFloat(String(data.total)) || 0;
  const shipping = parseFloat(String(data.shippingTotal ?? "0")) || 0;
  const tax = parseFloat(String(data.totalTax ?? "0")) || 0;
  const coupon = data.couponLines?.[0]?.code;

  const ecommerce: Record<string, unknown> = {
    transaction_id: String(orderId),
    value,
    currency: (data.currency || DEFAULT_CURRENCY).toUpperCase(),
    shipping,
    items,
  };
  if (tax > 0) ecommerce.tax = tax;
  if (coupon) ecommerce.coupon = coupon;

  pushEcommerceEvent("purchase", ecommerce);
  trackMetaPurchase({
    order_id: String(orderId),
    value,
    currency: (data.currency || DEFAULT_CURRENCY).toUpperCase(),
    content_ids: items.map((item) => item.item_id),
    content_type: "product",
  });
}
